/**
 * Isi & harga halaman jualan platform Kelolify (apex `kelolify.com`).
 *
 * MURNI — tanpa React, tanpa Supabase, tanpa I/O. Seluruh isinya fungsi atas
 * sebuah objek `PlatformPricing`, sehingga bisa diuji di sini apa adanya.
 *
 * KENAPA TERPISAH DARI KOMPONENNYA — dua alasan, keduanya soal "dua salinan
 * yang menyimpang":
 *
 * 1. **Harga hidup di tiga tempat sekaligus**: teks yang dibaca pengunjung
 *    ("Rp 1.200.000"), `<meta name="description">`, dan `price` di JSON-LD yang
 *    menuntut angka polos ("1200000"). Ditulis terpisah, cepat atau lambat satu
 *    berubah dan dua lainnya tertinggal — dan yang tertinggal adalah HARGA.
 *    Karena itu yang disimpan NOMINAL, string tampilan diturunkan darinya.
 *
 * 2. **FAQ wajib sama persis dengan yang terlihat.** Pedoman data terstruktur
 *    menuntut isi markup FAQ identik dengan isi halaman; menyalinnya ke dua
 *    tempat berarti suatu hari markup menjanjikan harga lama sementara halaman
 *    sudah berubah. Satu sumber, dua pembaca.
 *
 * HARGA KINI DATA, BUKAN KONSTANTA (mig 3510). Nilainya disunting pemilik
 * platform di `/platform/admin/settings` dan dibaca perender lewat
 * `fetchPlatformPricingServer()`. Konstanta di bawah tinggal **nilai bawaan**
 * yang dipakai saat setelan belum bisa dibaca — etalase harus tetap tayang saat
 * Supabase mati, dan halaman harga tanpa harga bukan halaman.
 *
 * ⛔ Karena itu berkas ini SENGAJA tidak lagi mengekspor string harga siap
 * pakai (dulu `PRICE_STANDARD` dkk). Konstanta tampilan yang bisa di-`import`
 * adalah undangan untuk merender angka mati di tempat baru, dan angka mati
 * itulah yang akan menyimpang dari setelan. Yang dirender selalu turunan dari
 * `PlatformPricing` yang sedang berlaku — lewat `platformPriceLabels()`.
 *
 * Diuji di `__tests__/platformLanding.test.ts`.
 */

import { PLATFORM_ICON_512 } from "@/lib/platformIcons";
import {
  DEFAULT_PLATFORM_SOCIAL,
  SOCIAL_PLATFORM_KEYS,
  type PlatformSocialLinks,
} from "@/lib/platformSocial";
import { toWaMeNumber } from "@/lib/siteSettings";

/**
 * Identitas platform — konstanta, BUKAN setelan toko.
 *
 * Nama TANPA TLD (2026-09-11, rebranding "MitraHub.id" → "Kelolify"). Domainnya
 * tetap `kelolify.com` dan tetap tercetak di kaki nota, canonical, dan
 * `robots.txt` — tapi lewat `platformBaseUrl()`, bukan lewat nama merek. Dua
 * generasi sebelumnya menempel TLD ke nama ("MitraHub.space" → "MitraHub.id"),
 * dan tiap kali domainnya pindah, nama merek ikut harus diganti di belasan
 * berkas. Pemisahan ini yang menghentikannya.
 */
export const PLATFORM_BRAND = "Kelolify";

/**
 * Satu penawaran langganan platform, utuh.
 *
 * Kuota & tenggat ikut di sini, bukan berdiri sendiri: kartu perintis membaca
 * sebagai satu kalimat ("Rp 700.000/bulan untuk 5 toko pertama, sampai 31
 * Desember 2026"), jadi ketiganya harus bisa berubah bersama. Memisahkannya
 * berarti pemilik bisa menurunkan harga sementara halaman tetap menjanjikan
 * tenggat yang sudah lewat.
 *
 * ⚠️ SENGAJA tak ada "sisa slot". Angka itu hanya benar bila dihitung dari
 * jumlah toko hidup; menuliskannya tangan berarti ia berbohong ke calon pembeli
 * berikutnya begitu satu toko masuk.
 */
export type PlatformPricing = {
  /** Nominal langganan per bulan (rupiah). */
  standardIdr: number;
  perintisIdr: number;
  /** Harga perpanjangan setelah kunci penawaran perintis berakhir. */
  perintisLanjutanIdr: number;
  /** Kuota toko yang kebagian harga perintis. */
  slotPerintis: number;
  /** Lama harga perintis dikunci, dalam bulan. */
  lockMonths: number;
  /** Batas penawaran, ISO `YYYY-MM-DD`. Label Indonesianya DITURUNKAN. */
  deadlineIso: string;
  /**
   * Lama masa coba gratis, dalam HARI (mig 4150). **0 = promonya
   * disembunyikan** dari seluruh halaman — banner, CTA, dan FAQ-nya ikut
   * menghilang; angka lain menuliskan dirinya di semua tempat itu sekaligus.
   * Bagian dari `PlatformPricing`, bukan setelan terpisah, karena ia SATU
   * penawaran dengan harga ("gratis N hari, lalu Rp X/bulan").
   */
  trialDays: number;
};

/**
 * Nilai bawaan — dipakai saat setelan belum terbaca (Supabase mati, migrasi
 * belum diterapkan, sesi belum siap).
 *
 * Angkanya harus tetap sama dengan nilai TERAKHIR yang ditulis migrasi: seed
 * mig 3510, diperbarui mig 4220 (699/899/1.199 rb). Halaman yang jatuh ke
 * fallback ini memajang harga ke calon pembeli — kalau ia tertinggal dari
 * setelan, yang dipajang adalah harga yang sudah tidak berlaku.
 */
export const DEFAULT_PLATFORM_PRICING: PlatformPricing = {
  standardIdr: 1_199_000,
  perintisIdr: 699_000,
  perintisLanjutanIdr: 899_000,
  slotPerintis: 5,
  lockMonths: 12,
  deadlineIso: "2026-12-31",
  trialDays: 30,
};

/**
 * "Rp 1.200.000" — pemisah ribuan titik, **spasi biasa (U+0020)** setelah "Rp".
 *
 * ⛔ SENGAJA tidak memakai `Intl.NumberFormat`/`toLocaleString`. Dua sebab:
 * (a) varian `currency: "IDR"` menyisipkan spasi TAK-PUTUS (U+00A0), yang
 * terlihat sama di layar tapi tidak cocok dengan pencocokan string apa adanya
 * di tes smoke; (b) keluarannya ikut berubah mengikuti versi ICU runtime.
 * Halaman harga butuh bentuk yang deterministik, bukan yang "biasanya begitu".
 *
 * Nilai non-hingga → "Rp 0"; negatif mempertahankan tandanya (bukan disunyikan
 * jadi positif — nominal negatif di sini berarti ada yang salah, dan itu harus
 * kelihatan).
 */
export function formatPlatformPrice(amount: number): string {
  if (!Number.isFinite(amount)) return "Rp 0";
  const n = Math.trunc(amount);
  const sign = n < 0 ? "-" : "";
  const grouped = String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}Rp ${grouped}`;
}

const BULAN_INDO = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

/**
 * "2026-12-31" → "31 Desember 2026".
 *
 * DITURUNKAN, tidak pernah ditulis tangan. Sebelum harga jadi data, label ini
 * konstanta terpisah yang kebetulan cocok dengan ISO-nya dan dijaga sebuah tes;
 * begitu tanggalnya bisa disunting lewat formulir, sepasang kolom "ISO" dan
 * "label" berarti mesin membaca satu tenggat sementara pembeli membaca tenggat
 * lain. Satu kolom, satu turunan.
 *
 * Dibaca dalam UTC supaya zona waktu perender tidak pernah menggeser harinya.
 * Tanggal tak terbaca dipulangkan apa adanya — lebih baik pengunjung melihat
 * "2026-13-40" yang jelas salah daripada "Invalid Date".
 */
export function formatIndoDate(iso: string): string {
  const s = (iso || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(`${s}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return s;
  // Bulan di luar 1–12 lolos regex tapi digulung `Date` ke bulan lain, jadi
  // kecocokan dicek ulang lewat bentuk ISO hasil bacaan.
  if (d.toISOString().slice(0, 10) !== s) return s;
  return `${d.getUTCDate()} ${BULAN_INDO[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Bentuk siap-render sebuah penawaran — semuanya turunan, tak ada yang ditulis tangan. */
export type PlatformPriceLabels = {
  standard: string;
  perintis: string;
  perintisLanjutan: string;
  deadline: string;
};

export function platformPriceLabels(p: PlatformPricing): PlatformPriceLabels {
  return {
    standard: formatPlatformPrice(p.standardIdr),
    perintis: formatPlatformPrice(p.perintisIdr),
    perintisLanjutan: formatPlatformPrice(p.perintisLanjutanIdr),
    deadline: formatIndoDate(p.deadlineIso),
  };
}

export type PlatformFaqItem = {
  q: string;
  a: string;
  /**
   * Label kecil di atas pertanyaan — penanda bahwa butir ini bicara soal paket
   * tertentu, bukan aturan umum.
   *
   * SENGAJA tidak ikut ke `Question.name` di JSON-LD: yang harus sama persis
   * antara markup dan halaman adalah PERTANYAANNYA. Label ini hiasan di
   * sebelahnya, dan menempelkannya ke nama pertanyaan justru membuat keduanya
   * menyimpang dari apa yang benar-benar ditanyakan orang.
   */
  label?: string;
};

/** Penanda paket berkuota — dipakai butir FAQ yang khusus soal perintis. */
export const LABEL_PERINTIS = "Paket Mitra Perintis · kuota terbatas";

/**
 * FAQ BERHARGA halaman platform — hanya butir yang kalimatnya turunan setelan
 * harga (trial, perintis, harga normal). Butir umum lainnya kini konten
 * editable (`copy_faq_extra`, mig 4190); yang berlaku dirakit SEKALI oleh
 * `resolvePlatformFaq()` di `platformCopy.ts` lalu diberikan ke kartu FAQ DAN
 * `platformJsonLd` — jangan menyalin daftar ini ke pembaca mana pun.
 */
export function platformPriceFaq(
  pricing: PlatformPricing = DEFAULT_PLATFORM_PRICING
): readonly PlatformFaqItem[] {
  const l = platformPriceLabels(pricing);
  return [
    {
      q: "Apa itu Harga Mitra Perintis?",
      label: LABEL_PERINTIS,
      a: `Harga khusus untuk ${pricing.slotPerintis} toko pertama yang bergabung: ${l.perintis}/bulan, bukan ${l.standard}. Berlaku sampai ${l.deadline} atau sampai kuotanya habis — mana yang lebih dulu.`,
    },
    {
      q: `Setelah ${pricing.lockMonths} bulan harganya jadi berapa?`,
      label: LABEL_PERINTIS,
      a: `Selama ${pricing.lockMonths} bulan pertama harga Anda terkunci di ${l.perintis}/bulan. Setelah itu ${l.perintisLanjutan}/bulan — dan tidak naik lagi selama langganan Anda tidak terputus.`,
    },
    {
      q: `Apakah ${l.standard}/bulan sudah termasuk semuanya?`,
      a: "Ya. Satu harga, semua fitur. Tidak ada paket bertingkat, tidak ada fitur yang dikunci, dan tidak ada potongan dari penjualan Anda.",
    },
  ];
}

/** Ringkasan platform — dipakai `<meta name="description">` DAN JSON-LD. */
export function platformDescription(
  pricing: PlatformPricing = DEFAULT_PLATFORM_PRICING
): string {
  const l = platformPriceLabels(pricing);
  return (
    "Sistem preorder & ready stock untuk distributor fashion: pesanan, " +
    "produksi, reseller, tagihan, dan pengiriman rapi dalam satu tempat — " +
    `tampil atas nama toko Anda sendiri. ${l.standard}/bulan, semua fitur. ` +
    `Harga Mitra Perintis ${l.perintis}/bulan untuk ${pricing.slotPerintis} ` +
    "toko pertama."
  );
}

/** Satu tawaran langganan bulanan dalam bentuk schema.org `Offer`. */
function monthlyOffer(opts: {
  name: string;
  amount: number;
  validUntil?: string;
}): Record<string, unknown> {
  const price = String(opts.amount);
  const offer: Record<string, unknown> = {
    "@type": "Offer",
    name: opts.name,
    price,
    priceCurrency: "IDR",
    availability: "https://schema.org/InStock",
    // `unitCode: "MON"` (UN/CEFACT) = per BULAN. Tanpa ini, angka 1.200.000
    // terbaca sebagai harga sekali bayar.
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price,
      priceCurrency: "IDR",
      unitCode: "MON",
      billingIncrement: 1,
    },
  };
  if (opts.validUntil) offer.priceValidUntil = opts.validUntil;
  return offer;
}

/**
 * Negara tempat Kelolify beroperasi — SATU-SATUNYA isi `address` Organization.
 * Putusan pemilik 2026-11-04: alamat publik cukup "Indonesia", tanpa kota/jalan.
 * Jangan menambah `streetAddress`/`addressLocality` tanpa putusan baru — alamat
 * di data terstruktur dibaca agen sebagai fakta, bukan hiasan.
 */
export const PLATFORM_ADDRESS_COUNTRY = "ID";

/**
 * Nomor penjualan dalam format internasional (`+62…`) untuk `telephone` di
 * JSON-LD; `""` bila nomornya tak bisa dibaca. Sumbernya nomor WA publik
 * platform (setelan `public_whatsapp`, mig 4150) — SATU nomor yang juga
 * dipajang di /kontak dan footer, jadi data terstruktur tak bisa menyimpang
 * dari yang dilihat pengunjung.
 */
export function platformTelephone(whatsapp: string): string {
  const digits = toWaMeNumber(whatsapp ?? "");
  return digits ? `+${digits}` : "";
}

/** Profil sosial yang terisi, urutan tetap — isi `sameAs`. Kolom kosong /
 *  tak lolos normalisasi (`mapPlatformSocialRow`) sudah "" dan dibuang. */
export function platformSameAs(social: PlatformSocialLinks): string[] {
  return SOCIAL_PLATFORM_KEYS.map((k) => social[k]).filter((u) => !!u);
}

/**
 * Simpul `Organization` — identitas Kelolify bagi mesin pencari & agen.
 *
 * `telephone` + `contactPoint` + `address` + `sameAs` (2026-11-04, kesiapan
 * agen): tanpa keempatnya agen yang ditanya "nomor Kelolify berapa / akunnya
 * yang mana" harus menebak dari HTML. Semua isinya turunan setelan yang SAMA
 * dengan yang dirender (WA + sosial dari `fetchPlatformSocialServer`); kolom
 * yang kosong dihilangkan, bukan ditulis "" — properti kosong lebih buruk
 * daripada properti yang tak ada.
 */
export function platformOrganizationJsonLd(
  baseUrl: string,
  social: PlatformSocialLinks = DEFAULT_PLATFORM_SOCIAL
): Record<string, unknown> {
  const base = (baseUrl || "").trim().replace(/\/+$/, "");
  const telephone = platformTelephone(social.whatsapp);
  const sameAs = platformSameAs(social);
  const org: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${base}/#organization`,
    name: PLATFORM_BRAND,
    url: `${base}/`,
    // Lambang PLATFORM, bukan ikon storefront: `logo` Organization inilah
    // yang dipungut mesin pencari sebagai wajah Kelolify.
    logo: `${base}${PLATFORM_ICON_512}`,
    address: { "@type": "PostalAddress", addressCountry: PLATFORM_ADDRESS_COUNTRY },
  };
  if (telephone) {
    org.telephone = telephone;
    org.contactPoint = [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        telephone,
        // Nomor ini dilayani lewat WhatsApp — tautan chat-nya ikut supaya
        // agen tidak menyarankan menelepon biasa.
        url: `https://wa.me/${telephone.slice(1)}`,
        areaServed: PLATFORM_ADDRESS_COUNTRY,
        availableLanguage: ["id"],
      },
    ];
  }
  if (sameAs.length > 0) org.sameAs = sameAs;
  return org;
}

/**
 * Graf JSON-LD halaman platform: `Organization` + `SoftwareApplication`
 * (ber-dua `Offer`) + `FAQPage`.
 *
 * ⚠️ Harga perpanjangan (setelah kunci penawaran berakhir) SENGAJA bukan
 * `Offer`. Ia bukan sesuatu yang bisa dibeli hari ini; mencantumkannya sebagai
 * tawaran ketiga akan terbaca mesin sebagai pilihan harga yang tersedia
 * sekarang. Angkanya tetap terbaca lewat teks FAQ, yang memang tempatnya.
 */
export function platformJsonLd(
  baseUrl: string,
  pricing: PlatformPricing = DEFAULT_PLATFORM_PRICING,
  // FAQ diterima DARI PEMANGGIL (hasil `resolvePlatformFaq`), bukan dihitung
  // sendiri: sejak sebagian butirnya konten editable (mig 4190), markup wajib
  // menerima daftar yang SAMA dengan yang dirender kartu FAQ. Bawaan ke butir
  // berharga saja supaya pemanggil lama/tes tetap sah.
  faq: readonly PlatformFaqItem[] = platformPriceFaq(pricing),
  // Sosial + WA publik (mig 4150) — isi `telephone`/`contactPoint`/`sameAs`.
  // Bawaan = nilai kode (sosial kosong, WA konstanta) untuk pemanggil lama/tes.
  social: PlatformSocialLinks = DEFAULT_PLATFORM_SOCIAL
): Record<string, unknown>[] {
  const base = (baseUrl || "").trim().replace(/\/+$/, "");
  const url = `${base}/`;
  const description = platformDescription(pricing);

  return [
    platformOrganizationJsonLd(base, social),
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "@id": `${base}/#software`,
      name: PLATFORM_BRAND,
      url,
      description,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      inLanguage: "id-ID",
      provider: { "@id": `${base}/#organization` },
      offers: [
        monthlyOffer({ name: "Harga normal", amount: pricing.standardIdr }),
        monthlyOffer({
          name: "Harga Mitra Perintis",
          amount: pricing.perintisIdr,
          validUntil: pricing.deadlineIso,
        }),
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${base}/#faq`,
      mainEntity: faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ];
}

/**
 * Isi `<script type="application/ld+json">` yang aman ditanam.
 *
 * `<` di-escape jadi `<`: sebuah `</script>` di dalam string JSON akan
 * MENUTUP tag skripnya dari dalam, dan sisa JSON tumpah jadi HTML. Sejak harga
 * & tenggat datang dari formulir (mig 3510), isi graf ini TIDAK lagi seluruhnya
 * konstanta jinak — penjaga ini kini benar-benar menahan sesuatu.
 */
export function jsonLdScriptText(value: unknown): string {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}
